import React from "react";
import {
  Alert,
  Box,
  Grid,
  TextField,
} from "@mui/material";
import {
  EditorSection,
  EditorSubsection,
  EditorFormNav,
  LinkListEditor,
} from "./institutional/InstitutionalEditorParts";
import { emptyContatoPage } from "../../utils/contatoPageContent";

function ChannelFields({ label, value, onChange }) {
  const ch = value || {};
  const set = (patch) => onChange({ ...ch, ...patch });
  return (
    <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label={`${label} — título do card`}
            value={ch.title || ""}
            onChange={(e) => set({ title: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Descrição"
            value={ch.description || ""}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Rótulo do botão / telefone"
            value={ch.label || ch.ctaLabel || ""}
            onChange={(e) => set({ label: e.target.value, ctaLabel: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Link (tel: ou URL)"
            value={ch.href || ""}
            onChange={(e) => set({ href: e.target.value })}
          />
        </Grid>
      </Grid>
  );
}

export default function ContatoEditor({ value, onChange }) {
  const v = value?.format === "contato.v1" ? value : emptyContatoPage();
  const set = (patch) => onChange({ ...v, ...patch });
  const setMeta = (patch) => set({ meta: { ...(v.meta || {}), ...patch } });
  const setHero = (patch) => set({ hero: { ...(v.hero || {}), ...patch } });
  const setChannels = (patch) => set({ channels: { ...(v.channels || {}), ...patch } });
  const setForm = (patch) => set({ form: { ...(v.form || {}), ...patch } });

  return (
    <Box sx={{ mt: 0.5 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Textos da página de contato — canais SAC, PcD, WhatsApp e formulário.{" "}
        <strong>Sem HTML</strong>.
      </Alert>

      <EditorFormNav
        sections={[
          { id: "sec-contato-google", label: "Google" },
          { id: "sec-contato-abertura", label: "Abertura" },
          { id: "sec-contato-canais", label: "Canais" },
          { id: "sec-contato-form", label: "Formulário" },
        ]}
      />

      <EditorSection
        id="sec-contato-google"
        title="Como a página aparece no Google"
        optional
        defaultExpanded={false}
      >
        <TextField
          fullWidth
          label="Título da página"
          value={v.meta?.title || ""}
          onChange={(e) => setMeta({ title: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Descrição curta"
          value={v.meta?.description || ""}
          onChange={(e) => setMeta({ description: e.target.value })}
        />
      </EditorSection>

      <EditorSection
        id="sec-contato-abertura"
        title="Abertura da página"
        subtitle="Título e botões no topo"
        defaultExpanded
      >
        <TextField
          fullWidth
          label="Título principal"
          value={v.hero?.title || ""}
          onChange={(e) => setHero({ title: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Texto de apoio"
          value={v.hero?.subtitle || ""}
          onChange={(e) => setHero({ subtitle: e.target.value })}
          sx={{ mb: 2 }}
        />
        <LinkListEditor
          label="Botões no topo (ex.: Buscar passagens)"
          items={v.hero?.ctaLinks}
          onChange={(ctaLinks) => setHero({ ctaLinks })}
        />
      </EditorSection>

      <EditorSection
        id="sec-contato-canais"
        title="Canais de atendimento"
        subtitle="Cards de SAC, PcD, WhatsApp e outros"
        defaultExpanded={false}
      >
        <TextField
          fullWidth
          size="small"
          label="Título da seção"
          value={v.channels?.sectionTitle || ""}
          onChange={(e) => setChannels({ sectionTitle: e.target.value })}
          placeholder="Canais de atendimento"
          sx={{ mb: 2 }}
        />
        <EditorSubsection title="SAC">
          <ChannelFields label="SAC" value={v.channels?.sac} onChange={(sac) => setChannels({ sac })} />
        </EditorSubsection>
        <EditorSubsection title="PcD (auditivo)">
          <ChannelFields label="PcD (auditivo)" value={v.channels?.pcd} onChange={(pcd) => setChannels({ pcd })} />
        </EditorSubsection>
        <EditorSubsection title="WhatsApp">
          <ChannelFields label="WhatsApp" value={v.channels?.whatsapp} onChange={(whatsapp) => setChannels({ whatsapp })} />
        </EditorSubsection>
        <EditorSubsection title="Agências">
          <ChannelFields label="Agências" value={v.channels?.agencies} onChange={(agencies) => setChannels({ agencies })} />
        </EditorSubsection>
        <EditorSubsection title="FAQ">
          <ChannelFields label="FAQ" value={v.channels?.faq} onChange={(faq) => setChannels({ faq })} />
        </EditorSubsection>
      </EditorSection>

      <EditorSection
        id="sec-contato-form"
        title="Formulário de contato"
        subtitle="Textos do formulário e da barra lateral"
        defaultExpanded={false}
      >
        <TextField
          fullWidth
          label="Título do formulário"
          value={v.form?.title || ""}
          onChange={(e) => setForm({ title: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Texto de apoio do formulário"
          value={v.form?.description || ""}
          onChange={(e) => setForm({ description: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Título da barra lateral"
          value={v.form?.sidebarTitle || ""}
          onChange={(e) => setForm({ sidebarTitle: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Nota da barra lateral"
          value={v.form?.sidebarNote || ""}
          onChange={(e) => setForm({ sidebarNote: e.target.value })}
        />
        <TextField
          fullWidth
          label="Título do FAQ da página"
          value={v.faqTitle || ""}
          onChange={(e) => onChange({ ...v, faqTitle: e.target.value })}
          sx={{ mt: 2 }}
        />
      </EditorSection>
    </Box>
  );
}
